import { useEffect, useState } from "react";
import { Axios } from "../../../API/Axios";
import { useLanguage } from "../../../Context/LanguageContext";
import "./AdminOrders.css";

const STATUS_VALUES = [ "pending", "processing", "shipped", "delivered", "cancelled" ];

const STATUS_CLASS = {
    pending: "ao-status-pending",
    processing: "ao-status-processing",
    shipped: "ao-status-shipped",
    delivered: "ao-status-delivered",
    cancelled: "ao-status-cancelled",
};

export default function AdminOrders ()
{
    const { t, lang } = useLanguage();

    const [ orders, setOrders ] = useState( [] );
    const [ loading, setLoading ] = useState( true );
    const [ search, setSearch ] = useState( "" );
    const [ statusFilter, setStatusFilter ] = useState( "" );
    const [ page, setPage ] = useState( 1 );
    const [ lastPage, setLastPage ] = useState( 1 );
    const [ updatingId, setUpdatingId ] = useState( null );
    const [ expandedId, setExpandedId ] = useState( null );
    // نص التتبع اللي بيكتبه الأدمن قبل الحفظ - key = order.id
    const [ trackingDrafts, setTrackingDrafts ] = useState( {} );
    const [ savingTrackingId, setSavingTrackingId ] = useState( null );

    function fetchOrders ()
    {
        setLoading( true );
        Axios.get( "/admin/orders", {
            params: {
                page,
                search: search || undefined,
                status: statusFilter || undefined,
            },
        } )
            .then( ( res ) =>
            {
                setOrders( res.data.data );
                setLastPage( res.data.last_page );
            } )
            .catch( () => setOrders( [] ) )
            .finally( () => setLoading( false ) );
    }

    useEffect( () =>
    {
        fetchOrders();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ page, statusFilter ] );

    function handleSearchSubmit ( e )
    {
        e.preventDefault();
        setPage( 1 );
        fetchOrders();
    }

    async function handleStatusChange ( orderId, newStatus )
    {
        setUpdatingId( orderId );
        try
        {
            const res = await Axios.put( `/admin/orders/${ orderId }/status`, { status: newStatus } );
            setOrders( ( prev ) =>
                prev.map( ( o ) => ( o.id === orderId ? res.data : o ) )
            );
        } catch ( error )
        {
            alert( t( 'adminOrders.updateError' ) );
        } finally
        {
            setUpdatingId( null );
        }
    }

    // يفتح فورم التتبع بقيم الطلب الحالية (أو فاضية لو أول مرة)
    function openTrackingForm ( order )
    {
        setTrackingDrafts( ( prev ) => ( {
            ...prev,
            [ order.id ]: {
                carrier_name: order.carrier_name || "",
                tracking_number: order.tracking_number || "",
                tracking_url: order.tracking_url || "",
            },
        } ) );
    }

    async function handleSaveTracking ( orderId )
    {
        const draft = trackingDrafts[ orderId ];
        if ( !draft ) return;

        setSavingTrackingId( orderId );
        try
        {
            const res = await Axios.put( `/admin/orders/${ orderId }/tracking`, draft );
            setOrders( ( prev ) =>
                prev.map( ( o ) => ( o.id === orderId ? res.data : o ) )
            );
        } catch ( error )
        {
            alert( t( 'adminOrders.trackingUpdateError' ) );
        } finally
        {
            setSavingTrackingId( null );
        }
    }

    return (
        <div className="admin-orders-page">
            <div className="page-header-block" style={ { textAlign: "start", padding: "0 0 20px" } }>
                <h1>{ t( 'adminOrders.pageTitle' ) }</h1>
                <p>{ t( 'adminOrders.pageSubtitle' ) }</p>
            </div>

            <div className="ao-toolbar">
                <form onSubmit={ handleSearchSubmit } className="ao-search-form">
                    <input
                        type="text"
                        placeholder={ t( 'adminOrders.searchPlaceholder' ) }
                        value={ search }
                        onChange={ ( e ) => setSearch( e.target.value ) }
                    />
                    <button type="submit">{ t( 'adminOrders.searchBtn' ) }</button>
                </form>

                <select
                    className="ao-status-filter"
                    value={ statusFilter }
                    onChange={ ( e ) =>
                    {
                        setStatusFilter( e.target.value );
                        setPage( 1 );
                    } }
                >
                    <option value="">{ t( 'adminOrders.allStatuses' ) }</option>
                    { STATUS_VALUES.map( ( s ) => (
                        <option key={ s } value={ s }>{ t( `adminOrders.status.${ s }` ) }</option>
                    ) ) }
                </select>
            </div>

            { loading ? (
                <p className="ao-empty-msg">{ t( 'adminOrders.loading' ) }</p>
            ) : orders.length === 0 ? (
                <p className="ao-empty-msg">{ t( 'adminOrders.noResults' ) }</p>
            ) : (
                <div className="ao-table-wrap">
                    <table className="ao-table">
                        <thead>
                            <tr>
                                <th>{ t( 'adminOrders.colOrderId' ) }</th>
                                <th>{ t( 'adminOrders.colCustomer' ) }</th>
                                <th>{ t( 'adminOrders.colItemsCount' ) }</th>
                                <th>{ t( 'adminOrders.colTotal' ) }</th>
                                <th>{ t( 'adminOrders.colDate' ) }</th>
                                <th>{ t( 'adminOrders.colStatus' ) }</th>
                                <th>{ t( 'adminOrders.colPayment' ) }</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            { orders.map( ( order ) => (
                                <>
                                    <tr key={ order.id }>
                                        <td>#{ order.id }</td>
                                        <td>
                                            <div className="ao-user-cell">
                                                <span>{ order.user?.name }</span>
                                                <small>{ order.user?.email }</small>
                                            </div>
                                        </td>
                                        <td>{ order.items?.length || 0 }</td>
                                        <td className="ao-total-cell">{ Number( order.total ).toFixed( 2 ) } { t( 'currency' ) }</td>
                                        <td>{ new Date( order.created_at ).toLocaleDateString( lang === 'ar' ? 'ar-EG' : 'en-US' ) }</td>
                                        <td>
                                            <select
                                                className={ `ao-status-select ${ STATUS_CLASS[ order.status ] }` }
                                                value={ order.status }
                                                disabled={ updatingId === order.id }
                                                onChange={ ( e ) => handleStatusChange( order.id, e.target.value ) }
                                            >
                                                { STATUS_VALUES.map( ( s ) => (
                                                    <option key={ s } value={ s }>{ t( `adminOrders.status.${ s }` ) }</option>
                                                ) ) }
                                            </select>
                                        </td>
                                        <td>
                                            <span className={ `od-payment-badge od-payment-badge--${ order.payment_status }` }>
                                                { t( `orders.paymentStatus.${ order.payment_status }` ) }
                                            </span>
                                        </td>
                                        <td>
                                            <button
                                                className="ao-expand-btn"
                                                onClick={ () =>
                                                {
                                                    const next = expandedId === order.id ? null : order.id;
                                                    setExpandedId( next );
                                                    if ( next ) openTrackingForm( order );
                                                } }
                                            >
                                                { expandedId === order.id ? t( 'adminOrders.hide' ) : t( 'adminOrders.details' ) }
                                            </button>
                                        </td>
                                    </tr>
                                    { expandedId === order.id && (
                                        <tr className="ao-detail-row">
                                            <td colSpan={ 8 }>
                                                <div className="ao-detail-items">
                                                    { order.items?.map( ( item ) =>
                                                    {
                                                        const img = item.product?.images?.[ 0 ]?.image;
                                                        return (
                                                            <div className="ao-detail-item" key={ item.id }>
                                                                { img ? (
                                                                    <img src={ img } alt={ item.product?.title } />
                                                                ) : (
                                                                    <div className="ao-detail-item-img-placeholder" />
                                                                ) }
                                                                <span>{ item.product?.title }</span>
                                                                <span>{ item.count } × { Number( item.price ).toFixed( 2 ) } { t( 'currency' ) }</span>
                                                            </div>
                                                        );
                                                    } ) }
                                                </div>

                                                <div className="ao-tracking-form">


                                                    <h4>{ t( 'adminOrders.trackingTitle' ) }</h4>
                                                    <div className="ao-tracking-inputs">
                                                        <input
                                                            type="text"
                                                            placeholder={ t( 'adminOrders.carrierPlaceholder' ) }
                                                            value={ trackingDrafts[ order.id ]?.carrier_name || "" }
                                                            onChange={ ( e ) => setTrackingDrafts( ( prev ) => ( {
                                                                ...prev,
                                                                [ order.id ]: { ...prev[ order.id ], carrier_name: e.target.value },
                                                            } ) ) }
                                                        />
                                                        <input
                                                            type="text"
                                                            placeholder={ t( 'adminOrders.trackingNumberPlaceholder' ) }
                                                            value={ trackingDrafts[ order.id ]?.tracking_number || "" }
                                                            onChange={ ( e ) => setTrackingDrafts( ( prev ) => ( {
                                                                ...prev,
                                                                [ order.id ]: { ...prev[ order.id ], tracking_number: e.target.value },
                                                            } ) ) }
                                                        />
                                                        <input
                                                            type="text"
                                                            placeholder={ t( 'adminOrders.trackingUrlPlaceholder' ) }
                                                            value={ trackingDrafts[ order.id ]?.tracking_url || "" }
                                                            onChange={ ( e ) => setTrackingDrafts( ( prev ) => ( {
                                                                ...prev,
                                                                [ order.id ]: { ...prev[ order.id ], tracking_url: e.target.value },
                                                            } ) ) }
                                                        />
                                                    </div>
                                                    <button
                                                        className="ao-tracking-save-btn"
                                                        onClick={ () => handleSaveTracking( order.id ) }
                                                        disabled={ savingTrackingId === order.id }
                                                    >
                                                        { savingTrackingId === order.id
                                                            ? t( 'adminOrders.saving' )
                                                            : t( 'adminOrders.saveTracking' ) }
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ) }
                                </>
                            ) ) }
                        </tbody>
                    </table>
                </div>
            ) }

            { lastPage > 1 && (
                <div className="ao-pagination">
                    <button
                        disabled={ page <= 1 }
                        onClick={ () => setPage( ( p ) => p - 1 ) }
                    >
                        { t( 'adminOrders.prev' ) }
                    </button>
                    <span>{ page } / { lastPage }</span>
                    <button
                        disabled={ page >= lastPage }
                        onClick={ () => setPage( ( p ) => p + 1 ) }
                    >
                        { t( 'adminOrders.next' ) }
                    </button>
                </div>
            ) }
        </div>
    );
}
